interface Crumb {
  name: string;
  path: string;
}

interface PageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: string;
  image?: string;
  breadcrumbs?: Crumb[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function buildUrl(path: string, basePath: string) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const prefix = basePath.replace(/\/$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${prefix}${suffix}`;
}

export default function PageMeta({
  title,
  description,
  keywords,
  path,
  type = "website",
  image,
  breadcrumbs,
  jsonLd,
}: PageMetaProps) {
  const basePath = __BASE_PATH__ || "/";
  const canonical = buildUrl(path, basePath);
  const graph: Record<string, unknown>[] = [];

  if (breadcrumbs && breadcrumbs.length > 0) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: buildUrl(crumb.path, basePath),
      })),
    });
  }

  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      graph.push(...jsonLd);
    } else {
      graph.push(jsonLd);
    }
  }

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content="index,follow" />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="黑料吃话" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {image ? <meta property="og:image" content={image} /> : null}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image ? <meta name="twitter:image" content={image} /> : null}
      {graph.length > 0 ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(graph.length === 1 ? graph[0] : graph) }}
        />
      ) : null}
    </>
  );
}